import { getCustomFontCssFamily } from '../utils/fontLoader.js';

/**
 * Centralized Font Options for Omnivaso Platform
 * Modern Google Fonts, Display, Luxury Serif, Script, and Monospace families.
 */
export const FONT_OPTIONS = [
    // ─── MODERN SANS-SERIF ───
    { label: "Modern Sans (Inter)", value: "Inter, system-ui, sans-serif", category: "Sans-Serif" },
    { label: "Sleek Sans (Outfit)", value: "Outfit, sans-serif", category: "Sans-Serif" },
    { label: "Bold Sans (Montserrat)", value: "Montserrat, sans-serif", category: "Sans-Serif" },
    { label: "Clean Sans (Roboto)", value: "Roboto, sans-serif", category: "Sans-Serif" },
    { label: "Friendly Sans (Poppins)", value: "Poppins, sans-serif", category: "Sans-Serif" },
    { label: "Corporate Sans (Plus Jakarta)", value: "'Plus Jakarta Sans', sans-serif", category: "Sans-Serif" },
    { label: "Futuristic Sans (Space Grotesk)", value: "'Space Grotesk', sans-serif", category: "Sans-Serif" },
    { label: "Minimal Sans (DM Sans)", value: "'DM Sans', sans-serif", category: "Sans-Serif" },
    { label: "Geometric Sans (Urbanist)", value: "Urbanist, sans-serif", category: "Sans-Serif" },
    { label: "Editorial Sans (Work Sans)", value: "'Work Sans', sans-serif", category: "Sans-Serif" },
    { label: "Tech Sans (Sora)", value: "Sora, sans-serif", category: "Sans-Serif" },
    { label: "Balanced Sans (Manrope)", value: "Manrope, sans-serif", category: "Sans-Serif" },
    { label: "Neutral Sans (Arial)", value: "Arial, sans-serif", category: "Sans-Serif" },

    // ─── HIGH IMPACT & DISPLAY ───
    { label: "Strong Condensed (Oswald)", value: "Oswald, sans-serif", category: "Display" },
    { label: "Artistic Avant-Garde (Syne)", value: "Syne, sans-serif", category: "Display" },
    { label: "Wide Futuristic (Syncopate)", value: "'Syncopate', sans-serif", category: "Display" },
    { label: "Poster Headlines (Bebas Neue)", value: "'Bebas Neue', sans-serif", category: "Display" },
    { label: "Impact Headlines (Anton)", value: "Anton, sans-serif", category: "Display" },
    { label: "Ultra Wide Modern (Unbounded)", value: "Unbounded, sans-serif", category: "Display" },

    // ─── LUXURY & EDITORIAL SERIFS ───
    { label: "Luxury Editorial (Playfair Display)", value: "'Playfair Display', serif", category: "Serif" },
    { label: "Literary Serif (Lora)", value: "Lora, serif", category: "Serif" },
    { label: "Readable Serif (Merriweather)", value: "Merriweather, serif", category: "Serif" },
    { label: "Roman Classic (Cinzel)", value: "Cinzel, serif", category: "Serif" },
    { label: "Decorative Roman (Cinzel Decorative)", value: "'Cinzel Decorative', serif", category: "Serif" },
    { label: "High Fashion Serif (Cormorant Garamond)", value: "'Cormorant Garamond', serif", category: "Serif" },
    { label: "Vogue Italian (Bodoni Moda)", value: "'Bodoni Moda', serif", category: "Serif" },
    { label: "Sophisticated Serif (Prata)", value: "Prata, serif", category: "Serif" },
    { label: "Vintage Display Serif (Fraunces)", value: "Fraunces, serif", category: "Serif" },
    { label: "Classic Serif (Times New Roman)", value: "'Times New Roman', serif", category: "Serif" },
    { label: "Elegant Serif (Georgia)", value: "Georgia, serif", category: "Serif" },

    // ─── CREATIVE & HANDWRITING ───
    { label: "Elegant Script (Dancing Script)", value: "'Dancing Script', cursive", category: "Script" },
    { label: "Casual Retro (Pacifico)", value: "Pacifico, cursive", category: "Script" },
    { label: "Handwritten Notes (Caveat)", value: "Caveat, cursive", category: "Script" },
    { label: "Monoline Script (Sacramento)", value: "Sacramento, cursive", category: "Script" },
    { label: "Vintage Title (Abril Fatface)", value: "'Abril Fatface', serif", category: "Display" },

    // ─── CODE & MONOSPACE ───
    { label: "Clean Mono (Roboto Mono)", value: "'Roboto Mono', monospace", category: "Monospace" },
    { label: "Sci-Fi Mono (Space Mono)", value: "'Space Mono', monospace", category: "Monospace" },
    { label: "Developer Code (Fira Code)", value: "'Fira Code', monospace", category: "Monospace" },
    { label: "Terminal Mono (JetBrains Mono)", value: "'JetBrains Mono', monospace", category: "Monospace" },
    { label: "Tech Mono (Courier New)", value: "'Courier New', monospace", category: "Monospace" }
];

/**
 * Build combined font list incorporating custom user fonts
 * @param {Array} customFonts - Array of custom fonts defined by the user
 * @param {boolean} includeInherit - Whether to prepend an "Inherit" option
 * @param {string} inheritValue - The value to assign to inherit option ("" or "inherit")
 * @param {string} inheritLabel - Custom label for the inherit option
 */
export const getFontOptions = (customFonts = [], includeInherit = false, inheritValue = "", inheritLabel = "Inherit (Default)") => {
    const list = [];
    if (includeInherit) {
        list.push({ label: inheritLabel, value: inheritValue, category: "Inherit" });
    }

    if (Array.isArray(customFonts) && customFonts.length > 0) {
        customFonts.forEach(cf => {
            if (!cf) return;
            const fontName = (cf.name || cf.label || '').trim().replace(/['"]/g, '');
            if (!fontName) return;
            const cssValue = cf.value || getCustomFontCssFamily(cf);
            list.push({
                label: `✨ ${fontName} (Custom)`,
                value: cssValue,
                category: "Custom Fonts",
                isCustom: true,
                rawName: fontName
            });
        });
    }

    return [...list, ...FONT_OPTIONS];
};

/**
 * Returns available font categories including Custom Fonts if any exist
 */
export const getFontCategories = (customFonts = []) => {
    const categories = [];
    if (Array.isArray(customFonts) && customFonts.length > 0) {
        categories.push("Custom Fonts");
    }
    categories.push("Sans-Serif", "Display", "Serif", "Script", "Monospace");
    return categories;
};

export default FONT_OPTIONS;
